import React from "react";
import { useNavigate } from "react-router-dom";
import { MdOutlinePerson } from "react-icons/md";
import Card from "components/card";
import StatusBadge from "components/ui/StatusBadge";
import { useLanguage } from "context/LanguageContext";
import {
  userPortalService,
  MeLearningResponse,
  LearningCourseItem,
  LearningSessionItem,
} from "services/userPortalService";
import type { SessionDetailTarget } from "./SessionDetailPage";

function formatDateTime(value: string | null, fallback: string): string {
  if (!value) return fallback;
  return new Date(value).toLocaleString("vi-VN");
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString("vi-VN");
}

function courseStatusTone(status: string): "success" | "info" | "neutral" {
  const normalized = status?.toUpperCase();
  if (normalized === "ONGOING") return "success";
  if (normalized === "OPENING") return "info";
  return "neutral";
}

function sessionStatusTone(
  status: string
): "info" | "success" | "warning" | "danger" | "neutral" {
  const normalized = status?.toUpperCase();
  if (normalized === "ACTIVE") return "success";
  if (normalized === "COMPLETED" || normalized === "CLOSED") return "neutral";
  if (normalized === "CANCELLED") return "danger";
  return "info";
}

// Prefer the human-readable venue name over the raw `location` string,
// which for legacy sessions can be raw GPS coordinates.
function sessionLocationLabel(session: LearningSessionItem): string | null {
  return session.venue_name || session.location || null;
}

const UserClasses = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = React.useState<MeLearningResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedCourse, setSelectedCourse] =
    React.useState<LearningCourseItem | null>(null);

  React.useEffect(() => {
    let active = true;
    userPortalService
      .getMyLearning()
      .then((res) => {
        if (active) setData(res);
      })
      .catch((err: Error) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const upcomingForSelectedCourse = React.useMemo<LearningSessionItem[]>(() => {
    if (!selectedCourse || !data) return [];
    return data.upcoming_sessions.filter(
      (session) => session.class_id === selectedCourse.class_id
    );
  }, [selectedCourse, data]);

  const pastForSelectedCourse = React.useMemo<LearningSessionItem[]>(() => {
    if (!selectedCourse || !data) return [];
    return data.past_sessions.filter(
      (session) => session.class_id === selectedCourse.class_id
    );
  }, [selectedCourse, data]);

  const courseStatusLabel = (status: string): string => {
    const normalized = status?.toUpperCase();
    if (normalized === "ONGOING") return t("classesPage.statusOngoing");
    if (normalized === "OPENING") return t("classesPage.statusOpening");
    if (normalized === "CLOSED") return t("classesPage.statusClosed");
    return status;
  };

  const sessionStatusLabel = (status: string): string => {
    const normalized = status?.toUpperCase();
    if (normalized === "ACTIVE") return t("classesPage.sessionStatusActive");
    if (normalized === "COMPLETED" || normalized === "CLOSED")
      return t("classesPage.sessionStatusCompleted");
    if (normalized === "CANCELLED")
      return t("classesPage.sessionStatusCancelled");
    return t("classesPage.sessionStatusScheduled");
  };

  const openSession = (session: LearningSessionItem) => {
    // Full page/route, not a popup — "không dùng popup cho buổi học, đi
    // sang trang mới". Title/time/location aren't fetchable from just the
    // session id (no GET /sessions/{id} for STUDENT callers), so they're
    // passed via router state; SessionDetailPage degrades gracefully if
    // this state is ever missing (e.g. a bookmarked/refreshed URL).
    const target: SessionDetailTarget = {
      id: session.session_id,
      title: session.title,
      start_time: session.start_time,
      end_time: session.end_time,
      location: session.location,
      venueName: session.venue_name,
    };
    navigate(`/user/classes/session/${session.session_id}`, {
      state: target,
    });
  };

  if (loading) {
    return (
      <div className="mt-6 text-sm text-ink-muted">
        {t("classesPage.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 rounded-lg bg-red-50 p-4 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-300">
        {error}
      </div>
    );
  }

  return (
    <div className="mt-5 flex flex-col gap-5">
      <h2 className="text-2xl font-bold text-ink">
        {t("classesPage.title")}
      </h2>

      <Card extra="w-full p-5">
        <p className="text-lg font-semibold text-ink">
          {t("classesPage.myCourses")}
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          {(data?.courses ?? []).map((course) => {
            const isSelected = selectedCourse?.class_id === course.class_id;
            const total = course.total_sessions ?? null;
            const remaining = course.remaining_sessions ?? null;
            const progressPercent =
              total && total > 0 && remaining !== null
                ? Math.min(
                    100,
                    Math.max(0, Math.round(((total - remaining) / total) * 100))
                  )
                : null;
            const startDate = formatDate(course.start_date);
            const endDate = formatDate(course.end_date);

            return (
              <button
                type="button"
                key={course.class_id}
                onClick={() => setSelectedCourse(course)}
                className={`rounded-xl border p-4 text-left transition-colors hover:border-brand-500 dark:hover:border-brand-400 ${
                  isSelected
                    ? "border-brand-500 bg-brand-50/50 dark:border-brand-400 dark:bg-brand-500/10"
                    : "border-line"
                }`}
              >
                <div className="flex items-start gap-3">
                  {course.instructor_avatar_url ? (
                    <img
                      src={course.instructor_avatar_url}
                      alt={course.instructor_name || ""}
                      className="h-11 w-11 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface-muted text-ink-muted">
                      <MdOutlinePerson className="h-6 w-6" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="truncate text-sm font-semibold text-ink">
                        {course.course_title}
                      </p>
                      {course.status && (
                        <StatusBadge
                          label={courseStatusLabel(course.status)}
                          tone={courseStatusTone(course.status)}
                          className="shrink-0 px-2 py-0.5 text-[10px]"
                        />
                      )}
                    </div>
                    <p className="mt-1 truncate text-xs text-ink-muted">
                      {t("classesPage.classCode")}: {course.class_code || "-"}
                    </p>
                    <p className="mt-1 truncate text-xs text-ink-muted">
                      {t("classesPage.instructor")}:{" "}
                      {course.instructor_name || t("classesPage.notAssigned")}
                    </p>
                    {course.center_name && (
                      <p className="mt-1 truncate text-xs text-ink-muted">
                        {t("classesPage.center")}: {course.center_name}
                      </p>
                    )}
                    {(startDate || endDate) && (
                      <p className="mt-1 truncate text-xs text-ink-muted">
                        {t("classesPage.period")}: {startDate || "?"} —{" "}
                        {endDate || "?"}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-ink-muted">
                    <span>{t("classesPage.remainingSessions")}</span>
                    <span>{course.remaining_sessions ?? "-"}</span>
                  </div>
                  {progressPercent !== null && (
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-muted">
                      <div
                        className="h-full rounded-full bg-brand-500"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        {(data?.courses?.length ?? 0) === 0 && (
          <p className="mt-3 text-sm text-ink-muted">
            {t("classesPage.noCourses")}
          </p>
        )}
      </Card>

      {selectedCourse && (
        <Card extra="w-full p-5">
          {(selectedCourse.instructor_bio ||
            selectedCourse.instructor_achievement ||
            selectedCourse.instructor_certificate) && (
            <div className="mb-4 rounded-xl border border-line p-4">
              <p className="text-sm font-semibold text-ink">
                {t("classesPage.instructorInfo")}
              </p>
              {selectedCourse.instructor_bio && (
                <p className="mt-2 text-xs text-ink-muted">
                  <span className="font-semibold">
                    {t("classesPage.bio")}:{" "}
                  </span>
                  {selectedCourse.instructor_bio}
                </p>
              )}
              {selectedCourse.instructor_achievement && (
                <p className="mt-2 text-xs text-ink-muted">
                  <span className="font-semibold">
                    {t("classesPage.achievement")}:{" "}
                  </span>
                  {selectedCourse.instructor_achievement}
                </p>
              )}
              {selectedCourse.instructor_certificate && (
                <p className="mt-2 text-xs text-ink-muted">
                  <span className="font-semibold">
                    {t("classesPage.certificate")}:{" "}
                  </span>
                  {selectedCourse.instructor_certificate}
                </p>
              )}
            </div>
          )}

          <p className="mb-3 text-lg font-semibold text-ink">
            {t("classesPage.sessionsTitle")}
          </p>
          {upcomingForSelectedCourse.length === 0 &&
          pastForSelectedCourse.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {t("classesPage.noSessions")}
            </p>
          ) : (
            <div className="flex flex-col gap-5">
              {upcomingForSelectedCourse.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase text-ink-muted">
                    {t("classesPage.upcomingSessions")}
                  </p>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {upcomingForSelectedCourse.map((session) => (
                      <button
                        type="button"
                        key={session.session_id}
                        onClick={() => openSession(session)}
                        className="rounded-lg border border-line p-3 text-left transition-colors hover:border-brand-500 dark:hover:border-brand-400"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-ink">
                            {session.title}
                          </p>
                          {session.status && (
                            <StatusBadge
                              label={sessionStatusLabel(session.status)}
                              tone={sessionStatusTone(session.status)}
                              className="shrink-0 px-2 py-0.5 text-[10px]"
                            />
                          )}
                        </div>
                        <p className="mt-1 text-xs text-ink-muted">
                          {formatDateTime(
                            session.start_time,
                            t("classesPage.notScheduled")
                          )}
                        </p>
                        {sessionLocationLabel(session) && (
                          <p className="mt-1 text-xs text-ink-muted">
                            {sessionLocationLabel(session)}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {pastForSelectedCourse.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase text-ink-muted">
                    {t("classesPage.pastSessions")}
                  </p>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {pastForSelectedCourse.map((session) => (
                      <button
                        type="button"
                        key={session.session_id}
                        onClick={() => openSession(session)}
                        className="rounded-lg border border-line p-3 text-left transition-colors hover:border-brand-500 dark:hover:border-brand-400"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-ink">
                            {session.title}
                          </p>
                          {session.status && (
                            <StatusBadge
                              label={sessionStatusLabel(session.status)}
                              tone={sessionStatusTone(session.status)}
                              className="shrink-0 px-2 py-0.5 text-[10px]"
                            />
                          )}
                        </div>
                        <p className="mt-1 text-xs text-ink-muted">
                          {formatDateTime(
                            session.start_time,
                            t("classesPage.notScheduled")
                          )}
                        </p>
                        {sessionLocationLabel(session) && (
                          <p className="mt-1 text-xs text-ink-muted">
                            {sessionLocationLabel(session)}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default UserClasses;
